import { Fragment, useState } from 'react';
import { alpha } from '@mui/material/styles';
import {
  Box,
  Chip,
  Collapse,
  IconButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  Category as SemiIcon,
  CheckCircleOutline as OkIcon,
  ErrorOutline as ErrorIcon,
  ExpandLess,
  ExpandMore,
  Texture as FabricIcon,
  WarningAmber as WarningIcon,
} from '@mui/icons-material';
import { MP_LABEL } from '../products/msAssortmentApi.mjs';
import { flattenChain, fmt, limitingOf, unitOf } from './model';
import TechcardSelect from './TechcardSelect';

const ROLES = {
  fabric: { label: 'Ткань', Icon: FabricIcon, color: 'secondary' },
  semi: { label: 'Полуфабрикат', Icon: SemiIcon, color: 'info' },
};

const ISSUE_TEXT = {
  no_rule: 'Добавка не считается',
  ambiguous: 'Нужно выбрать техкарту',
  unresolved: 'Параметрика не разрешилась',
};

function roleOf(role) {
  return ROLES[role] || ROLES.semi;
}

function accountLine(accounts) {
  if (!Array.isArray(accounts) || !accounts.length) return '';
  return accounts
    .map((account) => `${MP_LABEL[account.marketplace] || account.marketplace} · ${account.account_name}`)
    .join(', ');
}

function issueTitle(issues) {
  return issues
    .map((issue) => {
      const text = ISSUE_TEXT[issue.kind] || issue.kind;
      return issue.name ? `${text}: ${issue.name}` : text;
    })
    .join('\n');
}

function ChildStatus({ issues }) {
  const list = Array.isArray(issues) ? issues : [];
  if (!list.length) {
    return (
      <Tooltip title="Всё в порядке">
        <OkIcon fontSize="small" color="success" />
      </Tooltip>
    );
  }
  const hasError = list.some((issue) => issue.kind === 'unresolved');
  const Icon = hasError ? ErrorIcon : WarningIcon;
  return (
    <Tooltip title={<Box sx={{ whiteSpace: 'pre-line' }}>{issueTitle(list)}</Box>}>
      <Icon fontSize="small" color={hasError ? 'error' : 'warning'} />
    </Tooltip>
  );
}

function uncountedCaption(node) {
  if (node.counted) return null;
  if (node.plan_reason === 'ambiguous' || (node.cards?.length > 1 && !node.plan_id)) {
    return 'Не учитывается: выберите техкарту';
  }
  return 'Не учитывается';
}

function LimitCaption({ tree }) {
  const limit = limitingOf(tree);
  if (!limit) return null;
  const { node, others, path } = limit;
  const title = (
    <Box sx={{ whiteSpace: 'pre-line' }}>
      {path.map((step) => (
        `${step.name}: остаток ${fmt(step.stock)} ${unitOf(step.name)}`
        + `${step.make > 0 ? `, соберём +${fmt(step.make)}` : ''}`
        + `, норма ${fmt(step.qty)} ${unitOf(step.name)} → ${fmt(step.units_for_parent)} шт.`
      )).join('\n')}
    </Box>
  );
  return (
    <Tooltip title={title}>
      <Typography variant="caption" color="text.secondary" display="block" sx={{ cursor: 'help' }}>
        ограничивает: {node.name} — {fmt(node.units_for_parent)} шт.
        {others > 0 && ` (и ещё ${others})`}
      </Typography>
    </Tooltip>
  );
}

function ChainTable({ rows }) {
  return (
    <TableContainer>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>Материал</TableCell>
            <TableCell align="right">Норма</TableCell>
            <TableCell align="right">Остаток</TableCell>
            <TableCell align="right">Соберём</TableCell>
            <TableCell align="right">Хватит на</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map(({ node, depth }, index) => {
            const role = roleOf(node.role);
            const caption = uncountedCaption(node);
            return (
              <TableRow
                key={`${index}-${node.id}`}
                sx={(theme) => (node.limiting ? { bgcolor: alpha(theme.palette.warning.main, 0.12) } : {})}
              >
                <TableCell>
                  <Stack direction="row" spacing={1} alignItems="center" sx={{ pl: depth * 3 }}>
                    <Tooltip title={role.label}>
                      <role.Icon fontSize="small" color={role.color} />
                    </Tooltip>
                    <Box>
                      <Typography variant="body2">{node.name}</Typography>
                      {caption && (
                        <Typography variant="caption" color="warning.main" display="block">
                          {caption}
                        </Typography>
                      )}
                      {node.unresolved > 0 && (
                        <Typography variant="caption" color="error" display="block">
                          Параметрика не разрешилась: {node.unresolved}
                        </Typography>
                      )}
                    </Box>
                  </Stack>
                </TableCell>
                <TableCell align="right">{fmt(node.qty)} {unitOf(node.name)}</TableCell>
                <TableCell align="right">
                  {node.counted ? `${fmt(node.stock)} ${unitOf(node.name)}` : '—'}
                </TableCell>
                <TableCell align="right">
                  {node.counted && node.make > 0 ? `+${fmt(node.make)}` : '—'}
                </TableCell>
                <TableCell align="right">
                  {!node.counted && '—'}
                  {node.counted && node.limiting && (
                    <Tooltip title="Узкое место: этот материал ограничивает сборку">
                      <Chip size="small" color="warning" label={`${fmt(node.units_for_parent)} шт.`} />
                    </Tooltip>
                  )}
                  {node.counted && !node.limiting && `${fmt(node.units_for_parent)} шт.`}
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

export default function ChildrenTable({ rows, savingPlanId, onPlanChange }) {
  const [openId, setOpenId] = useState(null);
  const list = Array.isArray(rows) ? rows : [];
  const withSelect = typeof onPlanChange === 'function';
  const colSpan = withSelect ? 7 : 6;

  if (!list.length) {
    return (
      <Typography variant="body2" color="text.secondary">
        В семье нет модификаций со связью с маркетплейсом.
      </Typography>
    );
  }

  return (
    <TableContainer>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell padding="checkbox" />
            <TableCell>Модификация</TableCell>
            <TableCell align="right">Свой остаток</TableCell>
            <TableCell align="right">+ по техкартам</TableCell>
            <TableCell align="right">Итого для МП</TableCell>
            {withSelect && <TableCell sx={{ minWidth: 220 }}>Техкарта</TableCell>}
            <TableCell align="center">Статус</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {list.map((child) => {
            const chain = flattenChain(child.tree);
            const open = openId != null && String(openId) === String(child.ms_id);
            const accounts = accountLine(child.accounts);
            return (
              <Fragment key={child.ms_id}>
                <TableRow hover>
                  <TableCell padding="checkbox">
                    <IconButton
                      size="small"
                      aria-label={open ? 'Скрыть цепочку' : 'Показать цепочку'}
                      onClick={() => setOpenId(open ? null : child.ms_id)}
                    >
                      {open ? <ExpandLess fontSize="small" /> : <ExpandMore fontSize="small" />}
                    </IconButton>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">{child.name}</Typography>
                    {accounts && (
                      <Typography variant="caption" color="text.secondary" display="block">
                        {accounts}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell align="right">{fmt(child.own)}</TableCell>
                  <TableCell align="right">
                    <Typography
                      variant="body2"
                      component="span"
                      color={Number(child.extra) > 0 ? 'success.main' : 'text.primary'}
                    >
                      {Number(child.extra) > 0 ? `+${fmt(child.extra)}` : fmt(child.extra)}
                    </Typography>
                    <LimitCaption tree={child.tree} />
                  </TableCell>
                  <TableCell align="right">
                    <Typography variant="body2" component="span" fontWeight={700}>
                      {fmt(child.total)}
                    </Typography>
                  </TableCell>
                  {withSelect && (
                    <TableCell>
                      <TechcardSelect
                        node={child}
                        targetId={child.ms_id}
                        saving={Boolean(savingPlanId)}
                        onChange={onPlanChange}
                      />
                    </TableCell>
                  )}
                  <TableCell align="center">
                    <ChildStatus issues={child.issues} />
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell colSpan={colSpan} sx={{ py: 0, borderBottom: open ? undefined : 'none' }}>
                    <Collapse in={open} unmountOnExit>
                      <Box sx={{ py: 1.5 }}>
                        {chain.length ? (
                          <ChainTable rows={chain} />
                        ) : (
                          <Typography variant="body2" color="text.secondary">
                            Цепочка пуста — на МП уходит только свой остаток.
                          </Typography>
                        )}
                      </Box>
                    </Collapse>
                  </TableCell>
                </TableRow>
              </Fragment>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );
}
